import type { ReactNode } from 'react';
import type { WizardData } from '../types';
import { FRONT_PLACEMENTS, BACK_PLACEMENTS } from '../constants';

interface StepReviewProps {
  data: WizardData;
}

const NEXT_STEPS = (customerEmail: string) => [
  { title: 'Shop will be created', detail: 'Automatically in a few seconds' },
  { title: 'Products & Theme', detail: 'Preset per collection' },
  {
    title: 'Access via email',
    detail: `To ${customerEmail || 'the customer'}`,
  },
];

/** Inner panel of the review step: first paper on the section card. */
function ReviewPanel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-card bg-paper p-5 ring-1 ring-line">
      <h3 className="dc-h3 mb-4 text-base text-ink">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Step 5: Review & Launch
 * Shows summary of all entered data before submission
 */
export function StepReview({ data }: StepReviewProps) {
  const frontPlacementInfo = FRONT_PLACEMENTS.find(
    (p) => p.id === data.frontPlacement
  );
  const backPlacementInfo = BACK_PLACEMENTS.find(
    (p) => p.id === data.backPlacement
  );

  const reviewItems = [
    { label: 'Customer Email', value: data.customerEmail || '—' },
    { label: 'Customer Name', value: data.customerName || '—' },
    { label: 'Shop Name', value: data.shopName || '—' },
    { label: 'Language', value: data.language === 'de' ? 'German' : 'English' },
    {
      label: 'Shop URL',
      value: data.subdomain ? `${data.subdomain}.designdeck.shop` : '—',
    },
    {
      label: 'Collection',
      value: data.collectionName || '—',
    },
    {
      label: 'Front Design',
      value: data.frontDesign
        ? `${data.frontDesign.fileName} (${frontPlacementInfo?.label || '—'})`
        : '—',
    },
    {
      label: 'Back Design',
      value: data.backDesign
        ? `${data.backDesign.fileName} (${backPlacementInfo?.label || '—'})`
        : '—',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <ReviewPanel title="Summary">
        <dl className="space-y-3">
          {reviewItems.map((item) => (
            <div
              key={item.label}
              className="flex justify-between gap-4 text-sm"
            >
              <dt className="text-ink-2">{item.label}</dt>
              <dd className="max-w-[200px] truncate text-right font-medium text-ink">
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </ReviewPanel>

      <ReviewPanel title="What happens next?">
        <ol className="space-y-3 text-sm">
          {NEXT_STEPS(data.customerEmail).map((step, index) => (
            <li key={step.title} className="flex items-start gap-3">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-pill bg-primary-soft font-mono text-xs font-medium text-primary"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <div>
                <p className="font-medium text-ink">{step.title}</p>
                <p className="text-ink-2">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </ReviewPanel>
    </div>
  );
}
